import { z } from 'zod';

import { Id, IsoDateTime } from '../common/primitives';

export const AgentStatusSchema = z.enum(['ACTIVE', 'INACTIVE']);
export type AgentStatus = z.infer<typeof AgentStatusSchema>;

export const AgentSchema = z.object({
  id: Id,
  name: z.string(),
  email: z.string(),
  phone: z.string(),
  alternatePhone: z.string().nullable(),
  address: z.string(),
  addressProof: z.string().nullable(),
  cancelledCheque: z.string().nullable(),
  agentCode: z.string(),
  commissionBps: z.number().int(),
  approved: z.boolean(),
  status: AgentStatusSchema,
  createdAt: IsoDateTime,
});
export type Agent = z.infer<typeof AgentSchema>;

export const CreateAgentRequest = z
  .object({
    name: z.string().trim().min(2).max(120),
    email: z.string().trim().email(),
    phone: z.string().trim().min(10).max(20),
    alternatePhone: z.string().trim().max(20).optional(),
    address: z.string().trim().min(4).max(500),
    addressProof: z.string().trim().max(300).optional(),
    cancelledCheque: z.string().trim().max(300).optional(),
    commissionBps: z.number().int().min(0).max(10_000).optional(),
  })
  .strict();
export type CreateAgentRequest = z.infer<typeof CreateAgentRequest>;

export const UpdateAgentRequest = z
  .object({
    name: z.string().trim().min(2).max(120).optional(),
    phone: z.string().trim().min(10).max(20).optional(),
    alternatePhone: z.string().trim().max(20).optional(),
    address: z.string().trim().min(4).max(500).optional(),
    commissionBps: z.number().int().min(0).max(10_000).optional(),
    approved: z.boolean().optional(),
    status: AgentStatusSchema.optional(),
  })
  .strict();
export type UpdateAgentRequest = z.infer<typeof UpdateAgentRequest>;

export const AgentPayoutSettingSchema = z.object({
  payoutDueDate: z.string().nullable(),
  payoutDayOfMonth: z.number().int().nullable(),
  minPayoutPaise: z.number().int(),
  status: AgentStatusSchema,
  updatedAt: IsoDateTime,
});
export type AgentPayoutSetting = z.infer<typeof AgentPayoutSettingSchema>;

export const UpdateAgentPayoutSettingRequest = z
  .object({
    payoutDueDate: z.string().trim().max(200).optional(),
    /** Day of the month (1–28) the automatic payout run creates payouts; null turns it off. */
    payoutDayOfMonth: z.number().int().min(1).max(28).nullable().optional(),
    minPayoutPaise: z.number().int().min(100).max(10_000_000).optional(),
    status: AgentStatusSchema,
  })
  .strict();
export type UpdateAgentPayoutSettingRequest = z.infer<typeof UpdateAgentPayoutSettingRequest>;

// ── Programme ───────────────────────────────────────────────────────────────

/** A new customer names the agent who brought them in, by the agent's code. */
export const AttachAgentRequest = z.object({ code: z.string().trim().min(3).max(40) }).strict();
export type AttachAgentRequest = z.infer<typeof AttachAgentRequest>;

export const MarkAgentPayoutPaidRequest = z
  .object({
    /** Bank or UPI transaction reference (UTR). */
    reference: z.string().trim().min(4).max(100),
    note: z.string().trim().max(500).optional(),
  })
  .strict();
export type MarkAgentPayoutPaidRequest = z.infer<typeof MarkAgentPayoutPaidRequest>;

export const CancelAgentPayoutRequest = z
  .object({ note: z.string().trim().min(4).max(500) })
  .strict();
export type CancelAgentPayoutRequest = z.infer<typeof CancelAgentPayoutRequest>;

export const AgentCommissionStatusSchema = z.enum(['PENDING', 'IN_PAYOUT', 'PAID', 'CANCELLED']);
export type AgentCommissionStatus = z.infer<typeof AgentCommissionStatusSchema>;

export const AgentPayoutStatusSchema = z.enum(['PENDING', 'PAID', 'CANCELLED']);
export type AgentPayoutStatus = z.infer<typeof AgentPayoutStatusSchema>;

export interface AgentEarnings {
  /** Earned, not yet in a payout. */
  pendingPaise: number;
  /** In a payout the platform has not sent yet. */
  inPayoutPaise: number;
  paidPaise: number;
  lifetimePaise: number;
}

/** What an agent sees about themselves. */
export interface AgentMe {
  agentId: string;
  agentCode: string;
  name: string;
  commissionBps: number;
  approved: boolean;
  status: AgentStatus;
  kycStatus: string;
  customers: number;
  orders: number;
  earnings: AgentEarnings;
  payout: {
    dayOfMonth: number | null;
    note: string | null;
    minPayoutPaise: number;
    /** Why a payout cannot be requested right now; null when it can. */
    blockedReason: string | null;
  };
}

export interface AgentCommissionRow {
  id: string;
  orderNumber: string | null;
  basePaise: number;
  rateBps: number;
  amountPaise: number;
  status: AgentCommissionStatus;
  payoutId: string | null;
  createdAt: string;
}

export interface AgentPayoutRow {
  id: string;
  agentId: string;
  grossPaise: number;
  tdsPaise: number;
  amountPaise: number;
  financialYear: string | null;
  source: string;
  method: string | null;
  status: AgentPayoutStatus;
  reference: string | null;
  note: string | null;
  paidAt: string | null;
  createdAt: string;
  commissionCount: number;
}

/** An admin's view of a payout: who to pay, and where. */
export interface AdminAgentPayoutRow extends AgentPayoutRow {
  agent: { id: string; name: string; agentCode: string; phone: string };
  destination: {
    accountName: string | null;
    accountLast4: string | null;
    ifsc: string | null;
    upiId: string | null;
  } | null;
}

/** A customer an agent brought in, as the agent may see them. */
export interface AgentCustomerRow {
  /** First name only — an agent is not given a customer's contact details. */
  firstName: string;
  joinedAt: string;
  orders: number;
  earnedPaise: number;
}
